import React, { useState } from 'react';
import { Upload, File, CheckCircle, Eye } from 'lucide-react';

interface DocumentUploadProps {
    documents: any[];
    onUploadSuccess: () => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ documents, onUploadSuccess }) => {
    const [uploading, setUploading] = useState<string | null>(null);

    const docTypes = [
        { id: 'passport', label: 'Valid Passport', required: true },
        { id: 'academic', label: 'Academic Records', required: true },
        { id: 'financial', label: 'Financial Proof', required: true },
        { id: 'english', label: 'English Test Results', required: true },
        { id: 'other', label: 'Supporting Documents', required: false },
    ];

    const getDocStatus = (type: string) => {
        const doc = documents.find(d => d.Type === type);
        return doc ? doc.Status : 'missing';
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const file = event.target.files[0];
        setUploading(type);

        const formData = new FormData();
        formData.append(type, file);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/student/upload', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) throw new Error('Upload failed');

            onUploadSuccess();
        } catch (error) {
            console.error(error);
            alert('Upload failed');
        } finally {
            setUploading(null);
        }
    };

    const handleView = async (docId: number) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/student/document/view/${docId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch document');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            console.error(error);
            alert('Could not preview document');
        }
    };

    const getStatusOrb = (status: string) => {
        if (status === 'verified') return { color: '#00e5d0', label: 'Verified' };
        if (status === 'rejected') return { color: '#ba1a1a', label: 'Rejected' };
        if (status !== 'missing') return { color: '#f59e0b', label: status };
        return null;
    };

    return (
        <div className="rounded-2xl p-10" style={{ background: '#ffffff', boxShadow: '0 8px 32px rgba(25,28,30,0.06)' }}>
            {/* Secure Asset Header */}
            <div className="mb-2">
                <p className="label-editorial mb-2" style={{ color: '#6b7280', letterSpacing: '0.1em' }}>
                    SECURE ASSET MANAGEMENT
                </p>
                <h3 className="text-2xl font-display font-bold mb-2" style={{ color: '#191c1e' }}>
                    Upload Your Documents
                </h3>
                <p className="text-sm font-ui leading-relaxed" style={{ color: '#6b7280' }}>
                    Please provide the required documentation to finalize your admission profile.
                </p>
            </div>

            {/* Encryption badge */}
            <div className="flex items-center gap-2 mb-10">
                <span className="w-2 h-2 rounded-full animate-pulseOrb" style={{ background: '#00e5d0' }} />
                <span className="text-xs font-ui font-semibold" style={{ color: '#009B8F' }}>Live Encryption Active</span>
            </div>

            {/* Document Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {docTypes.map(doc => {
                    const status = getDocStatus(doc.id);
                    const isUploaded = status !== 'missing';
                    const orbInfo = getStatusOrb(status);

                    return (
                        <div
                            key={doc.id}
                            className="rounded-xl p-6 transition-all duration-200"
                            style={{
                                background: isUploaded ? '#f7f9fb' : '#ffffff',
                                boxShadow: isUploaded ? 'none' : 'inset 0 0 0 1px rgba(196,198,200,0.15)',
                            }}
                        >
                            {/* Icon */}
                            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4"
                                style={{ background: '#e6e8ea' }}>
                                <File size={18} style={{ color: '#003434' }} strokeWidth={1.8} />
                            </div>

                            {/* Badge */}
                            <div className="flex items-center gap-2 mb-3">
                                {doc.required ? (
                                    <span className="text-[9px] font-ui font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                                        style={{ background: 'rgba(0,155,143,0.08)', color: '#009B8F' }}>
                                        Required
                                    </span>
                                ) : (
                                    <span className="text-[9px] font-ui font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                                        style={{ background: '#f2f4f6', color: '#9ca3af' }}>
                                        Optional
                                    </span>
                                )}
                            </div>

                            {/* Label */}
                            <h4 className="text-sm font-display font-semibold mb-1" style={{ color: '#191c1e' }}>
                                {doc.label}
                            </h4>

                            {/* Status */}
                            <div className="flex items-center gap-2 mb-5">
                                {orbInfo ? (
                                    <>
                                        <span className="w-2 h-2 rounded-full" style={{ background: orbInfo.color }} />
                                        <span className="label-editorial" style={{ color: orbInfo.color === '#00e5d0' ? '#009B8F' : orbInfo.color }}>
                                            {orbInfo.label}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span className="w-2 h-2 rounded-full" style={{ background: '#ba1a1a' }} />
                                        <span className="label-editorial" style={{ color: '#ba1a1a' }}>Missing</span>
                                    </>
                                )}
                            </div>

                            {/* Action area */}
                            {isUploaded ? (
                                <div className="flex items-center gap-3">
                                    <button
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-ui font-semibold transition-all"
                                        style={{ background: 'rgba(0,155,143,0.08)', color: '#009B8F' }}
                                        onClick={() => handleView(documents.find(d => d.Type === doc.id)?.ID)}
                                    >
                                        <Eye size={13} /> View
                                    </button>
                                    <button
                                        className="text-xs font-ui transition-all"
                                        style={{ color: '#9ca3af' }}
                                        onClick={() => document.getElementById(`file-${doc.id}`)?.click()}
                                    >
                                        Replace
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="flex items-center gap-2 px-4 py-2 rounded-md text-xs font-ui font-semibold transition-all duration-200"
                                    style={{
                                        background: uploading === doc.id ? '#e6e8ea' : 'linear-gradient(135deg, #003434 0%, #004d4d 100%)',
                                        color: uploading === doc.id ? '#9ca3af' : '#ffffff',
                                        cursor: uploading === doc.id ? 'not-allowed' : 'pointer',
                                    }}
                                    onClick={() => {
                                        if (uploading !== doc.id) document.getElementById(`file-${doc.id}`)?.click();
                                    }}
                                >
                                    <Upload size={13} />
                                    {uploading === doc.id ? 'Uploading...' : 'Upload'}
                                </button>
                            )}

                            <input
                                type="file"
                                id={`file-${doc.id}`}
                                className="hidden"
                                onChange={(e) => handleFileChange(e, doc.id)}
                                disabled={uploading === doc.id}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DocumentUpload;
